import { useResourceCheckSummaries as useApiResourceCheckSummaries } from '@my/check-api/hooks';
import { useMemo } from 'react';

// ────────────────────────────────────────────────────────────────────────────
// Walk-around check roll-up per resource.
//
// This is the Check-specific half of the Resources grid — the "Last Check"
// and "Status" columns that replaced TMS's Compliance / Compliant pair.
// Resources themselves are shared master data from My.Directory; their
// *check* history belongs to My.Check.
//
// Live against `/check/api/v1/checks/resource-summaries`, which returns the
// latest SUBMITTED check per resource. An in-progress check is not reported:
// showing its outcome would tell an operator a vehicle passed before the
// driver had finished looking at it.
//
// The vocabulary is deliberately not the API's. The server has three outcomes
// (Pass / Defect / Advisory) because a row only exists for a resource that has
// been checked — "never checked" is the ABSENCE of a row, not a fourth value.
// The grid needs a word for that absence, so `NotDone` lives on this side of
// the boundary and `checkSummaryFor` is where it is applied.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Outcome of a resource's most recent walk-around check.
 *
 *  - `Pass`     — checked, no defects recorded.
 *  - `Defect`   — checked, one or more defects recorded.
 *  - `Advisory` — checked, non-blocking advisories only.
 *  - `NotDone`  — no check on record.
 *
 * Kept in step with the `status` block in `locales/en/resources.json`.
 * The first three mirror the server's `CheckOutcome` exactly; `NotDone` is
 * this layer's own — see the file header.
 */
export type CheckStatusValue = 'Pass' | 'Defect' | 'Advisory' | 'NotDone';

export interface ResourceCheckSummary {
  /** ISO date of the most recent check, or null when never checked. */
  lastCheckedAt: string | null;
  status: CheckStatusValue;
}

/** What a resource with no check history looks like. */
const NEVER_CHECKED: ResourceCheckSummary = {
  lastCheckedAt: null,
  status: 'NotDone',
};

/**
 * Check roll-ups for every resource in the org, keyed by resource id.
 *
 * Returns a Map rather than a query result: every call site is a grid row
 * asking "what about me", and the columns render the same either way while
 * the request is in flight — a resource with no entry yet reads as Not done,
 * which is also what it reads as when the answer arrives and it has never
 * been checked.
 */
export function useResourceCheckSummaries(
  organisationId: string | null,
): Map<string, ResourceCheckSummary> {
  const { data } = useApiResourceCheckSummaries(organisationId);

  return useMemo(() => {
    const map = new Map<string, ResourceCheckSummary>();
    if (!data) return map;
    for (const [resourceId, row] of data) {
      map.set(resourceId, { lastCheckedAt: row.lastCheckedUtc, status: row.outcome });
    }
    return map;
  }, [data]);
}

/** Roll-up for one resource, falling back to "never checked". */
export function checkSummaryFor(
  summaries: Map<string, ResourceCheckSummary>,
  resourceId: string,
): ResourceCheckSummary {
  return summaries.get(resourceId) ?? NEVER_CHECKED;
}
